import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiUtils } from "../../../utils/apiUtils";
import useRepGuardClients from "./useRepGuardClients";

const PLATFORM_OPTIONS = [
  { value: "x", label: "X" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "reddit", label: "Reddit" },
];

const CATEGORY_OPTIONS = [
  { value: "hate_speech", label: "Hate Speech" },
  { value: "harassment", label: "Harassment" },
  { value: "explicit", label: "Explicit" },
  { value: "violence", label: "Violence" },
  { value: "political", label: "Political" },
  { value: "cultural", label: "Cultural" },
  { value: "misconduct", label: "Misconduct" },
  { value: "drug_use", label: "Drug Use" },
  { value: "self_harm", label: "Self Harm" },
  { value: "spam", label: "Spam / Misinformation" },
  { value: "other", label: "Other" },
];

const DECISION_OPTIONS = [
  { value: "warn", label: "Warn" },
  { value: "require_review", label: "Require review" },
  { value: "block", label: "Block" },
];

function parseCommaSeparatedKeywords(value) {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export default function RepGuardPolicyAdmin() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [permissions, setPermissions] = useState({
    canEditPolicy: false,
    role: "viewer",
  });
  const [form, setForm] = useState({
    supportedPlatforms: ["x"],
    enabledCategories: CATEGORY_OPTIONS.map((option) => option.value),
    sensitivityLevel: "medium",
    decisionThresholds: {
      warn: 25,
      requireReview: 50,
      block: 75,
    },
    blockedCategories: ["hate_speech", "violence", "self_harm"],
    hardBlockKeywords: "",
    allowUserOverrideOn: ["warn"],
    requireReasonOnOverride: true,
    cacheTtlMs: 15000,
  });
  const {
    clients,
    selectedClientId,
    setSelectedClientId,
    loadingClients,
    clientError,
  } = useRepGuardClients();

  const platformSummary = useMemo(
    () => form.supportedPlatforms.map((platform) => platform.toUpperCase()).join(", ") || "None",
    [form.supportedPlatforms]
  );

  const loadPolicy = useCallback(async () => {
    if (!selectedClientId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");
      const response = await apiUtils.get("/api/extension/config", {
        params: { clientId: selectedClientId },
      });
      const data = response.data?.data;
      const policy = data?.policy;
      setPermissions(data?.permissions || { canEditPolicy: false, role: "viewer" });

      if (!policy) {
        throw new Error("No RepGuard policy returned from the server.");
      }

      setForm({
        supportedPlatforms: policy.supportedPlatforms || ["x"],
        enabledCategories: policy.enabledCategories || CATEGORY_OPTIONS.map((option) => option.value),
        sensitivityLevel: policy.sensitivityLevel || "medium",
        decisionThresholds: {
          warn: policy.decisionThresholds?.warn ?? 25,
          requireReview: policy.decisionThresholds?.requireReview ?? 50,
          block: policy.decisionThresholds?.block ?? 75,
        },
        blockedCategories: policy.blockedCategories || [],
        hardBlockKeywords: (policy.hardBlockKeywords || []).join(", "),
        allowUserOverrideOn: policy.allowUserOverrideOn || ["warn"],
        requireReasonOnOverride: policy.requireReasonOnOverride !== false,
        cacheTtlMs: policy.cacheTtlMs ?? 15000,
      });
    } catch (loadError) {
      setError(loadError.message || "Failed to load RepGuard policy.");
    } finally {
      setLoading(false);
    }
  }, [selectedClientId]);

  useEffect(() => {
    if (selectedClientId) {
      loadPolicy();
    }
  }, [loadPolicy, selectedClientId]);

  const toggleArrayValue = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((entry) => entry !== value)
        : [...prev[field], value],
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");
      setSuccessMessage("");

      const payload = {
        clientId: selectedClientId,
        supportedPlatforms: form.supportedPlatforms,
        enabledCategories: form.enabledCategories,
        sensitivityLevel: form.sensitivityLevel,
        decisionThresholds: {
          warn: Number(form.decisionThresholds.warn),
          requireReview: Number(form.decisionThresholds.requireReview),
          block: Number(form.decisionThresholds.block),
        },
        blockedCategories: form.blockedCategories,
        hardBlockKeywords: parseCommaSeparatedKeywords(form.hardBlockKeywords),
        allowUserOverrideOn: form.allowUserOverrideOn,
        requireReasonOnOverride: form.requireReasonOnOverride,
        cacheTtlMs: Number(form.cacheTtlMs),
      };

      await apiUtils.put("/api/extension/policy", payload);
      setSuccessMessage("RepGuard policy saved.");
      await loadPolicy();
    } catch (saveError) {
      setError(saveError.message || "Failed to save RepGuard policy.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="border-t border-border pt-6 first:border-0 first:pt-0">
        <p className="text-muted-foreground">Loading RepGuard policy...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="border-t border-border pt-6 first:border-0 first:pt-0">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">RepGuard Policy</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Manage extension thresholds, supported platforms, blocked categories, and override rules.
            </p>
          </div>
          <div className="text-sm text-muted-foreground">
            Active platforms: <span className="font-medium text-foreground">{platformSummary}</span>
          </div>
        </div>

        <div className="mt-4">
          <label className="text-sm font-medium text-foreground block mb-2">Client policy scope</label>
          <select
            value={selectedClientId}
            onChange={(event) => setSelectedClientId(event.target.value)}
            disabled={loadingClients || clients.length === 0}
            className="w-full max-w-md rounded-lg border border-input px-3 py-2"
          >
            {clients.length === 0 ? (
              <option value="">No clients available</option>
            ) : (
              clients.map((client) => (
                <option key={client._id} value={client._id}>
                  {client.name}
                </option>
              ))
            )}
          </select>
          {clientError && <p className="mt-2 text-sm text-red-600">{clientError}</p>}
        </div>

        {error && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-200">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="mt-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700 dark:bg-green-500/10 dark:border-green-500/30 dark:text-green-200">
            {successMessage}
          </div>
        )}

        {!permissions.canEditPolicy && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-200">
            Your role is <span className="font-semibold">{permissions.role}</span>. You can view this client policy, but only admins and managers can save changes.
          </div>
        )}

        <fieldset
          className="mt-6 grid gap-6 lg:grid-cols-2"
          disabled={!permissions.canEditPolicy}
        >
          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">Platforms and sensitivity</h3>

            <div>
              <p className="text-sm font-medium text-foreground mb-2">Supported platforms</p>
              <div className="grid gap-2 sm:grid-cols-3">
                {PLATFORM_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={form.supportedPlatforms.includes(option.value)}
                      onChange={() => toggleArrayValue("supportedPlatforms", option.value)}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-foreground block mb-2">Sensitivity level</label>
              <select
                value={form.sensitivityLevel}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, sensitivityLevel: event.target.value }))
                }
                className="w-full rounded-lg border border-input px-3 py-2"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-foreground block mb-2">Hard-block keywords</label>
              <textarea
                rows={4}
                value={form.hardBlockKeywords}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, hardBlockKeywords: event.target.value }))
                }
                className="w-full rounded-lg border border-input px-3 py-2"
                placeholder="Comma separated terms, e.g. lawsuit, leak, doxxing"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Any draft containing these terms will be blocked immediately.
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-foreground block mb-2">Cache TTL (ms)</label>
              <input
                type="number"
                min="1000"
                max="120000"
                value={form.cacheTtlMs}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    cacheTtlMs: event.target.value,
                  }))
                }
                className="w-full rounded-lg border border-input px-3 py-2"
              />
            </div>
          </section>

          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">Decision thresholds</h3>

            <div className="grid gap-4 sm:grid-cols-3">
              <label className="text-sm font-medium text-foreground">
                Warn
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={form.decisionThresholds.warn}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      decisionThresholds: {
                        ...prev.decisionThresholds,
                        warn: event.target.value,
                      },
                    }))
                  }
                  className="mt-2 w-full rounded-lg border border-input px-3 py-2"
                />
              </label>
              <label className="text-sm font-medium text-foreground">
                Require review
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={form.decisionThresholds.requireReview}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      decisionThresholds: {
                        ...prev.decisionThresholds,
                        requireReview: event.target.value,
                      },
                    }))
                  }
                  className="mt-2 w-full rounded-lg border border-input px-3 py-2"
                />
              </label>
              <label className="text-sm font-medium text-foreground">
                Block
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={form.decisionThresholds.block}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      decisionThresholds: {
                        ...prev.decisionThresholds,
                        block: event.target.value,
                      },
                    }))
                  }
                  className="mt-2 w-full rounded-lg border border-input px-3 py-2"
                />
              </label>
            </div>

            <div>
              <p className="text-sm font-medium text-foreground mb-2">Enabled categories</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {CATEGORY_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={form.enabledCategories.includes(option.value)}
                      onChange={() => toggleArrayValue("enabledCategories", option.value)}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-foreground mb-2">Blocked categories</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {CATEGORY_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={form.blockedCategories.includes(option.value)}
                      onChange={() => toggleArrayValue("blockedCategories", option.value)}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-foreground mb-2">Override allowed on</p>
              <div className="grid gap-2 sm:grid-cols-3">
                {DECISION_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={form.allowUserOverrideOn.includes(option.value)}
                      onChange={() => toggleArrayValue("allowUserOverrideOn", option.value)}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-3 rounded-lg border border-border px-4 py-3 text-sm text-foreground">
              <input
                type="checkbox"
                checked={form.requireReasonOnOverride}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    requireReasonOnOverride: event.target.checked,
                  }))
                }
              />
              Require users to provide a reason when overriding a warning
            </label>
          </section>
        </fieldset>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !permissions.canEditPolicy}
            className="rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-neutral-400"
          >
            {saving ? "Saving..." : "Save RepGuard Policy"}
          </button>
        </div>
      </div>
    </div>
  );
}
